// package imports
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormHelperText, Grid } from '@barracuda-internal/bds-core'
import { Controller } from 'react-hook-form'

// TODO: eliminate imports from "old" code paths (components/..., lib/...}, move things to V2
// V1 imports
import { useFormatMessage } from 'lib/localization'
import ScheduleMatrix from 'components/libs/scheduler/ScheduleMatrix'
import { FormRow, FormSpacer, HelperText, InlineRadioGroup, PageTitle } from 'components/libs/layout/pages/pageLayout'
import { SettingsPageContainer } from 'components/libs/layout/pages/SettingsPageContainer'
import styles from 'components/pages/users/quarantineNotification/quarantineNotificationStyles'
import { GROUP_TITLE, TOPICS, useSupportModalLogic } from 'components/pages/support/useSupportModalLogic'
import UsersQuarantineSupport from 'components/pages/support/help/usersQuarantine/UsersQuarantine'
import { setErrorSnackBar, setSuccessSnackBar } from 'redux/features/app/appSlice'
import { useAppDispatch } from 'redux/toolkit/hooks'
import WarningDialog from 'components/libs/dialog/warningDialog/WarningDialog'

// V2 module imports
import { IEgdApiClient, useForm, useFormState, useI18n } from 'v2/modules/lib'
import { IQuarantineNotificationForm } from './quarantineNotificationForm'

const BASE_I18N_KEY = 'ess.users.quarantine_notification'
const DIALOG_I18N_KEY = 'ess.dialog'

export interface QuarantineNotificationPageProps {
  settingsForm: IQuarantineNotificationForm
  accountId: string
  domainId: number | null
  egdApiClient: IEgdApiClient
}

export const QuarantineNotificationPage: React.FC<QuarantineNotificationPageProps> = ({
  settingsForm,
  accountId,
  domainId,
  egdApiClient
}) => {
  const dispatch = useAppDispatch()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatDialogMessage = useFormatMessage(DIALOG_I18N_KEY)
  const classes = styles()
  const [onOpenSupport, HelpModal] = useSupportModalLogic({
    content: UsersQuarantineSupport,
    title: TOPICS.userQuarantineNotification,
    groupTitle: GROUP_TITLE.userTopics
  })

  const { t } = useI18n()
  const canSetState = useRef(true)
  const [isVerifyingDomainConflicts, setIsVerifyingDomainConflicts] = useState(false)
  const [isDomainConflictDialogOpen, setIsDomainConflictDialogOpen] = useState(false)
  const [numberOfConflictingDomains, setNumberOfConflictingDomains] = useState(0)
  const [isResetPending, setIsResetPending] = useState(false)
  const form = useForm({
    initialData: settingsForm.form.getInitialValues(),
    load: async () => settingsForm.form.load(),
    validate: async () => {
      // Not implemented yet
    },
    save: async (dirtyFormData: Record<string, any>) => {
      try {
        await settingsForm.form.save(dirtyFormData)
        dispatch(setSuccessSnackBar({ message: 'success_changing_settings' }))
      } catch (e) {
        dispatch(setErrorSnackBar({ message: 'error_changing_settings' }))
      }
    },
    schema: settingsForm.schema
  })

  const formState = useFormState(form)

  const isInProgress = useMemo(
    () => formState.isLoading || !formState.isReady,
    [formState.isLoading, formState.isReady]
  )

  // TODO: porting the module to a headless app cleans up extensive inline code in this UI component
  const onClickSave = useCallback(async () => {
    try {
      if (!domainId) {
        setIsVerifyingDomainConflicts(true)
        const domainModifiedSettings = await egdApiClient.accountService.getDomainModifiedSettings(accountId)
        const conflictingDomainIds: number[] = []
        Object.keys(formState.dirtyFields).forEach(inputName => {
          const inputAdapters = settingsForm.form.adapters.getByInputName(inputName)
          inputAdapters.forEach(adapter => {
            const nextList: number[] = domainModifiedSettings[adapter.variable] || []
            conflictingDomainIds.push(...nextList)
          })
        })
        if (!canSetState.current) {
          return
        }
        setIsVerifyingDomainConflicts(false)
        if (new Set(conflictingDomainIds).size) {
          setIsDomainConflictDialogOpen(true)
          setNumberOfConflictingDomains(new Set(conflictingDomainIds).size)
          return
        }
      }
      await form.save()
    } catch {
      if (!canSetState.current) {
        return
      }
      setIsVerifyingDomainConflicts(false)
      dispatch(setErrorSnackBar({ message: 'error_changing_settings' }))
    }
  }, [
    accountId,
    dispatch,
    domainId,
    egdApiClient.accountService,
    form,
    formState.dirtyFields,
    settingsForm.form.adapters
  ])

  useEffect(() => {
    canSetState.current = true
    return () => {
      canSetState.current = false
    }
  }, [])

  return useMemo(
    () => (
      <SettingsPageContainer
        isContentLoading={isInProgress}
        isFailedToLoad={formState.isFailed}
        dirtyCheck
        isDirtyForm={formState.isDirty}
      >
        <WarningDialog
          open={isDomainConflictDialogOpen}
          title={formatDialogMessage('domain_settings_conflict', { numberOfDomains: numberOfConflictingDomains })}
          description={formatDialogMessage('domain_settings_conflict_description')}
          onCancel={() => {
            setIsDomainConflictDialogOpen(false)
            setNumberOfConflictingDomains(0)
          }}
          onConfirm={async () => {
            await form.save()
            if (canSetState.current) {
              setIsDomainConflictDialogOpen(false)
              setNumberOfConflictingDomains(0)
            }
          }}
          isPending={formState.isSaving}
        />
        <HelpModal />
        <PageTitle
          title={formatMessage('title')}
          onOpenSupport={onOpenSupport}
          onCancel={() => setIsResetPending(true)}
          onReset={() => form.reset(formState.defaultValues)}
          isResetPending={isResetPending}
          closeWarningDialog={() => setIsResetPending(false)}
          onSubmit={onClickSave}
          submitInProgress={formState.isSaving || isVerifyingDomainConflicts}
          isDisabledSubmit={!formState.isDirty || isInProgress || settingsForm.form.isReadonly}
        />
        <FormRow rowTitle={formatMessage('quarantine_messages_for_intent_analysis')}>
          <Controller
            control={form.control}
            name="isEnabledMessagesForIntentAnalysis"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineRadioGroup
                  name="isEnabledMessagesForIntentAnalysis"
                  value={value}
                  onChange={onChange}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormRow rowTitle="">
          <HelperText>{formatMessage('quarantine_messages_for_intent_analysis_help')}</HelperText>
        </FormRow>

        <FormRow rowTitle={formatMessage('default_interval')}>
          <Controller
            control={form.control}
            name="defaultInterval"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineRadioGroup
                  name="defaultInterval"
                  value={value}
                  onChange={onChange}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                  labels={[formatMessage('never'), formatMessage('scheduled')]}
                  values={['never', 'custom']}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>

        <FormRow rowTitle={formatMessage('scheduled_interval')} />
        <Grid container direction="column">
          <Grid item className={classes.scheduledMatrixContainer}>
            <Controller
              control={form.control}
              name="customInboundSchedule"
              render={({ field: { onChange, value }, fieldState }) => (
                <>
                  <ScheduleMatrix
                    isEnabled={form.getValues().defaultInterval === 'custom' && !settingsForm.form.isReadonly}
                    customInboundSchedule={{ ...value }}
                    doUpdate={schedule => {
                      onChange({ target: { value: schedule } })
                    }}
                  />
                  {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
                </>
              )}
            />
            <Grid item className={classes.helperTextContainer}>
              <HelperText>{formatMessage('scheduled_interval_help')}</HelperText>
            </Grid>
          </Grid>
        </Grid>

        <FormRow rowTitle={formatMessage('allow_specify_interval')}>
          <Controller
            control={form.control}
            name="isAllowSpecifyInterval"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineRadioGroup
                  name="isAllowSpecifyInterval"
                  value={value}
                  onChange={onChange}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>

        <FormRow rowTitle={formatMessage('allow_temp_passcode')}>
          <Controller
            control={form.control}
            name="isTempPasscodeEnabled"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineRadioGroup
                  name="isTempPasscodeEnabled"
                  value={value}
                  onChange={onChange}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormRow rowTitle="">
          <HelperText>{formatMessage('allow_temp_passcode_help')}</HelperText>
        </FormRow>
        <FormSpacer />
        <FormSpacer />
      </SettingsPageContainer>
    ),
    [
      isInProgress,
      formState.isFailed,
      formState.isDirty,
      formState.isSaving,
      formState.defaultValues,
      isDomainConflictDialogOpen,
      formatDialogMessage,
      numberOfConflictingDomains,
      HelpModal,
      formatMessage,
      onOpenSupport,
      isResetPending,
      onClickSave,
      isVerifyingDomainConflicts,
      settingsForm.form.isReadonly,
      form,
      classes.scheduledMatrixContainer,
      classes.helperTextContainer,
      t
    ]
  )
}
